import { useMutation } from "@apollo/client";
import { yupResolver } from "@hookform/resolvers/yup";
import { useCallback, useMemo, useState } from "react";
import { useForm } from "react-hook-form";
import { useIntl } from "react-intl";
import * as yup from "yup";

import BaseErrors from "@components/fields/BaseErrors";
import DirectUploadFileInput from "@components/fields/DirectUploadFileInput";
import FormButtons from "@components/fields/FormButtons";
import Input from "@components/fields/Input";
import Select from "@components/fields/Select";
import Form from "@components/forms/Form";
import useCreateFileResource from "@hooks/mutations/useCreateFileResource";
import {
  FileResourceFragment,
  FileResourceUpdateDocument,
  FileResourceUpdateMutation,
  FileResourceUpdateMutationVariables,
  FolderFragment,
  GetJourneyWithDocumentsQuery
} from "@typing/Generated";
import { handleMutationResponse } from "@utils/formUtils";
import { sortByLabel } from "@utils/modelUtils";

type FormData = {
  blobId: string;
  folderId: string | null;
  title: string;
};

type Props = {
  autofocus: boolean;
  editKind?: "folder" | "name";
  fileResource?: FileResourceFragment | null;
  folder?: FolderFragment | null;
  journey: GetJourneyWithDocumentsQuery["journey"];
  onCancel: () => void;
  onSuccess: () => void;
};

const FileResourceForm = ({ autofocus, editKind, fileResource, folder, journey, onCancel, onSuccess }: Props) => {
  const intl = useIntl();
  const defaultValues: FormData = {
    blobId: "",
    folderId: fileResource?.folder?.id ?? folder?.id ?? "",
    title: fileResource?.title
      ? fileResource.title
      : fileResource?.pdfType
        ? intl.formatMessage({ id: `models.pdfType.${fileResource.pdfType}` })
        : ""
  };

  const [baseErrors, setBaseErrors] = useState<string[]>([]);

  const [fileInputBusy, setFileInputBusy] = useState<boolean>(false);

  const createFileResource = useCreateFileResource(journey.id);

  const [updateFileResource] = useMutation<FileResourceUpdateMutation, FileResourceUpdateMutationVariables>(
    FileResourceUpdateDocument
  );

  const schema: yup.ObjectSchema<FormData> = yup
    .object()
    .shape({
      blobId: fileResource?.id
        ? yup.string().defined()
        : yup.string().required(intl.formatMessage({ id: "errors.fileResource.blobId.blank" })),
      folderId: yup.string().nullable().defined(),
      title: yup.string().required(intl.formatMessage({ id: "errors.fileResource.title.blank" }))
    })
    .required();

  const form = useForm<FormData>({
    defaultValues,
    mode: "onChange",
    resolver: yupResolver(schema)
  });

  const onSubmit = useCallback(async () => {
    setBaseErrors([]);

    if (fileResource?.id) {
      return updateFileResource({
        variables: {
          ...form.getValues(),
          id: fileResource.id
        }
      }).then(response => {
        handleMutationResponse<FormData>({
          data: response.data?.fileResourceUpdate,
          intl,
          onSuccess,
          setBaseErrors,
          setError: form.setError,
          txBase: "errors.fileResource"
        });
      });
    }
    return createFileResource({
      variables: {
        ...form.getValues(),
        journeyId: journey.id
      }
    }).then(response => {
      handleMutationResponse<FormData>({
        data: response.data?.fileResourceCreate,
        intl,
        onSuccess,
        setBaseErrors,
        setError: form.setError,
        txBase: "errors.fileResource"
      });
    });
  }, [createFileResource, fileResource?.id, form, intl, journey.id, onSuccess, updateFileResource]);

  const folderOptions = useMemo(() => {
    const folders = sortByLabel(
      journey.folders.map(folder => ({
        label: folder.title ?? intl.formatMessage({ id: `models.folderKind.${folder.kind}` }),
        value: folder.id
      }))
    );

    folders.unshift({
      label: intl.formatMessage({ id: "models.folderKind.myFiles" }),
      value: ""
    });
    return folders;
  }, [intl, journey.folders]);

  const handleFileSelected = useCallback(
    (filenames: string[]) => {
      if (!form.getValues().title) {
        form.setValue("title", filenames[0], { shouldTouch: true });
      }
    },
    [form]
  );

  return (
    <Form hookForm={form} onSubmit={form.handleSubmit(onSubmit)}>
      <BaseErrors errors={baseErrors} />
      {editKind !== "folder" && (
        <Input
          autofocus={autofocus}
          error={form.formState.errors.title}
          label={intl.formatMessage({ id: "forms.fileResource.titleLabel" })}
          register={form.register("title")}
          required
        />
      )}

      {!fileResource && (
        <div>
          <DirectUploadFileInput<FormData, string>
            control={form.control}
            errors={form.formState.errors.blobId ? [form.formState.errors.blobId] : []}
            label={intl.formatMessage({
              id: fileResource ? "forms.fileResource.updateBlobIdLabel" : "forms.fileResource.blobIdLabel"
            })}
            name="blobId"
            onBusyChange={setFileInputBusy}
            onFileSelected={handleFileSelected}
            required={!fileResource}
            setValue={value => {
              form.setValue("blobId", value);
            }}
          />
        </div>
      )}
      {editKind !== "name" && (
        <Select<FormData>
          control={form.control}
          disabled={!!(folder && !fileResource)}
          error={form.formState.errors.folderId}
          label={intl.formatMessage({
            id: fileResource ? "forms.fileResource.updateFolderIdLabel" : "forms.fileResource.folderIdLabel"
          })}
          name="folderId"
          options={folderOptions}
        />
      )}

      <FormButtons
        hookForm={form}
        onCancel={onCancel}
        saveDisabled={fileInputBusy}
        saveKey={fileResource ? undefined : "forms.fileResource.create"}
      />
    </Form>
  );
};

export default FileResourceForm;
